import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import InstagramActivityCard from '../InstagramActivityCard';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Toont bij een lege feed een gerangschikt lijstje "wat past het beste bij
 * jouw zoekvraag". Roept /api/activities/suggestions aan met de huidige
 * filters, backend doet de relevance-scoring. Logt ook stil de 0-resultaat
 * filter-combi naar /api/activities/empty-search-log.
 *
 * Props:
 *   filters:       object met wijk / activity_type / target_audience / date_filter / cost_filter
 *   onClearFilters: () => void
 *   variant:       'empty' | 'append' — 'empty' vervangt de card, 'append' hangt
 *                  onder een niet-lege lijst voor "Ook interessant voor jou".
 */
const EmptyStateSuggestions = ({ filters, onClearFilters, variant = 'empty', excludeIds = [] }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (filters?.wijk && filters.wijk !== 'Heel Almere') params.append('wijk', filters.wijk);
    if (filters?.activity_type) params.append('activity_type', filters.activity_type);
    if (filters?.target_audience && filters.target_audience !== 'all') params.append('target_audience', filters.target_audience);
    if (excludeIds.length > 0) params.append('exclude_ids', excludeIds.slice(0, 20).join(','));
    params.append('limit', variant === 'empty' ? '8' : '4');

    axios.get(`${API}/api/activities/suggestions?${params}`)
      .then(res => { if (!cancelled) setItems(res.data.suggestions || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Log alleen bij empty-variant (echte 0-resultaat momenten).
    if (variant === 'empty') {
      const token = localStorage.getItem('hoibuur_token');
      axios.post(`${API}/api/activities/empty-search-log`, {
        wijk: filters?.wijk, activity_type: filters?.activity_type,
        target_audience: filters?.target_audience, date_filter: filters?.date_filter,
        cost_filter: filters?.cost_filter,
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : {}).catch(() => {});
    }

    return () => { cancelled = true; };
  }, [filters?.wijk, filters?.activity_type, filters?.target_audience, variant]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;

  // Contextuele intro-regel (gebruikt door zowel de fallback als de header).
  const parts = [];
  if (filters?.activity_type) parts.push(filters.activity_type);
  if (filters?.wijk && filters.wijk !== 'Heel Almere') parts.push(`in ${filters.wijk}`);
  const contextLabel = parts.length > 0 ? parts.join(' ') : '';

  // Expliciete "geen resultaten in gekozen wijk"-zin (Kirsten, aug 2026).
  const wijkLabel = (filters?.wijk && filters.wijk !== 'Heel Almere') ? filters.wijk : '';
  const noResultLine = wijkLabel
    ? `Op dit moment zijn er geen activiteiten die voldoen aan jouw zoekcriteria in ${wijkLabel}, maar hieronder vind je vergelijkbaar aanbod in ${wijkLabel} en op andere plekken in Almere.`
    : 'Op dit moment zijn er geen activiteiten die precies aan jouw zoekcriteria voldoen, maar deze sluiten er het beste op aan.';

  // Empty-state fallback: /suggestions kan óók 0 resultaten geven (bv. bij
  // hele smalle combos). Voor 'append'-variant renderen we niets, voor
  // 'empty'-variant tonen we een tekst-fallback zodat de wrapper-Card in
  // Home.jsx niet leeg lijkt.
  if (!items || items.length === 0) {
    if (variant === 'append') return null;
    return (
      <div className="text-center" data-testid="home-empty-fallback">
        <div className="w-12 h-12 rounded-full bg-hoi-coral/15 mx-auto mb-3 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-hoi-coral" aria-hidden="true" />
        </div>
        <h3 className="font-heading font-bold text-lg text-primary mb-1" data-testid="home-empty-title">
          {wijkLabel ? `Nog niets in ${wijkLabel}` : 'Geen activiteiten met deze filters'}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {noResultLine} Probeer een andere wijk of dag, of organiseer zelf iets in de buurt.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button variant="outline" onClick={onClearFilters} className="rounded-full" data-testid="home-empty-clear-btn">
            <X className="w-4 h-4 mr-2" /> Alle filters wissen
          </Button>
          <Link to="/activiteit-toevoegen">
            <Button className="rounded-full bg-hoi-coral hover:bg-hoi-coral/90 text-white" data-testid="home-empty-organize-btn">
              Of organiseer zelf <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (variant === 'append') {
    return (
      <section className="mt-10 pt-8 border-t border-slate-200" data-testid="home-related-suggestions">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-hoi-coral" aria-hidden="true" />
          <h2 className="text-base md:text-lg font-heading font-semibold text-primary">Ook interessant voor jou</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.slice(0, 4).map((a) => (
            <InstagramActivityCard key={a.id} activity={a} />
          ))}
        </div>
      </section>
    );
  }

  // variant === 'empty'
  return (
    <div className="max-w-4xl" data-testid="home-empty-suggestions">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-hoi-coral/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-hoi-coral" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-xl text-primary mb-1" data-testid="home-empty-title">
            {wijkLabel ? `Nog geen aanbod in ${wijkLabel}` : 'Dit past het beste bij jouw zoekvraag'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {noResultLine}{' '}
            <button onClick={onClearFilters} className="underline text-hoi-blue hover:text-hoi-blue/80" data-testid="home-empty-clear-inline">
              Wis alle filters
            </button>
            {' '}om het volledige aanbod te zien.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {items.map((a) => (
          <InstagramActivityCard key={a.id} activity={a} />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button variant="outline" onClick={onClearFilters} className="rounded-full" data-testid="home-empty-clear-btn">
          <X className="w-4 h-4 mr-2" /> Alle filters wissen
        </Button>
        <Link to="/activiteit-toevoegen">
          <Button className="rounded-full bg-hoi-coral hover:bg-hoi-coral/90 text-white" data-testid="home-empty-organize-btn">
            Of organiseer zelf <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default EmptyStateSuggestions;
